import { supportsHLS } from './hls';

declare global {
  interface Window {
    WebKitMediaSource: any;
    WebKitSourceBuffer: any;
  }
}

export type KeySystem = 'widevine' | 'playready' | 'fairplay';

export function isChromiumEdgeOnWindows(userAgent: string) {
  // "Old Edge" has the string "Edge" in the user agent string, and new Edge has the string "Edg".
  return userAgent.includes('Edg') && !userAgent.includes('Edge') && userAgent.includes('Windows NT');
}

const isPlayreadySupported = () => {
  const isOldEdge = !!(window as any).MSMediaKeys;
  return isOldEdge;
};

const isPlayreadyActivated = async () => {
  const keyOptions = [
    {
      initDataTypes: ['cenc'],
      audioCapabilities: [{ contentType: 'audio/mp4;codecs="mp4a.40.2"' }],
      videoCapabilities: [{ contentType: 'video/mp4;codecs="avc1.42E01E"' }],
    },
  ];

  try {
    await window.navigator.requestMediaKeySystemAccess('com.microsoft.playready', keyOptions);
    return true;
  } catch (err) {
    return false;
  }
};

const isWidevineSupported = () => {
  return !!(Boolean(window.navigator.requestMediaKeySystemAccess) && MediaKeySystemAccess.prototype.getConfiguration);
};

const isWidevineActivated = async () => {
  const keyOptions = [
    {
      initDataTypes: ['keyids', 'webm'],
      audioCapabilities: [
        {
          contentType: 'audio/webm; codecs="opus"',
          robustness: 'SW_SECURE_CRYPTO',
        },
        {
          contentType: 'audio/webm; codecs="vorbis"',
          robustness: 'SW_SECURE_CRYPTO',
        },
      ],
      videoCapabilities: [
        {
          contentType: 'video/webm; codecs="vp9"',
          robustness: 'SW_SECURE_CRYPTO',
        },
        {
          contentType: 'video/webm; codecs="vp8"',
          robustness: 'SW_SECURE_CRYPTO',
        },
      ],
    },
  ];

  try {
    await window.navigator.requestMediaKeySystemAccess('com.widevine.alpha', keyOptions);
    return true;
  } catch (err) {
    return false;
  }
};

export const browserKeySystem = (): KeySystem | null => {
  if (supportsHLS() && navigator.vendor.match(/apple/i)) {
    return 'fairplay';
  } else if (isPlayreadySupported()) {
    return 'playready';
  } else if (isWidevineSupported()) {
    return 'widevine';
  }
  return null;
};

export const isEncryptedMediaExtensionActivatedForKeySystem = async (keySystem: KeySystem | null): Promise<boolean> => {
  switch (keySystem) {
    case 'playready':
      return isPlayreadyActivated();
    case 'widevine':
      return isWidevineActivated();
    case 'fairplay':
      // Did not find any possibility for checking/disabling fairplay for Safari.
      return Promise.resolve(true);
    default:
      return Promise.resolve(false);
  }
};

export const isLicenseUrlValid = (licenseUrl: string) => {
  try {
    const { searchParams } = new URL(licenseUrl);
    const validToEpoch = Date.parse(searchParams.get('validto') ?? '0');
    if (validToEpoch > 0 && Number.isFinite(validToEpoch)) {
      const expiresInMs = validToEpoch - Date.now();
      return expiresInMs > 3_000; // demand 3s validity for network conditions
    }
  } catch {
    /* om nom nom */
  }

  return false;
};
